import React from 'react';
import { useAccessibilityStore } from '../../stores/accessibilityStore';

/**
 * Screen-reader accessible data table that renders alongside or instead of a chart.
 * Automatically shown when `showDataTables` is enabled in accessibility settings,
 * or always rendered as sr-only for assistive technology.
 */
interface Column {
  key: string;
  label: string;
  format?: (value: any) => string;
}

interface AccessibleDataTableProps {
  /** Unique label describing the chart for screen readers */
  caption: string;
  columns: Column[];
  data: Record<string, any>[];
  /** When true, always visually show the table */
  forceVisible?: boolean;
  className?: string;
}

export function AccessibleDataTable({
  caption,
  columns,
  data,
  forceVisible = false,
  className = '',
}: AccessibleDataTableProps) {
  const showDataTables = useAccessibilityStore((s) => s.showDataTables);
  const isVisible = forceVisible || showDataTables;

  if (data.length === 0) return null;

  return (
    <div
      className={isVisible ? `mt-3 ${className}` : 'sr-only'}
      role="region"
      aria-label={`Data table: ${caption}`}
    >
      {isVisible && (
        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 font-medium dark:text-gray-500">
          Data Table
        </div>
      )}
      <table className={isVisible
        ? 'w-full text-xs border-collapse border border-gray-200 dark:border-gray-700'
        : 'w-full'
      }>
        <caption className={isVisible ? 'sr-only' : ''}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={isVisible
                  ? 'px-2 py-1.5 text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-medium text-gray-600 dark:text-gray-300'
                  : ''
                }
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={isVisible
                    ? 'px-2 py-1 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                    : ''
                  }
                >
                  {col.format ? col.format(row[col.key]) : String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
